import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { dateFnsLocales } from 'shared-locales';
import { getFormattedDate } from 'lib/common';
import { Button, DisplayFormattedDate } from 'components/common';
import { Dropdown } from 'components/common/dropdown';

import { CalendarApi } from '@fullcalendar/core';

interface Props {
  calendarApi: CalendarApi;
}

const CalendarEventCalendarHeader = ({ calendarApi }: Props) => {
  const { language, messages, theme } = useReduxState();

  const [isToday, setIsToday] = useState(true);
  const [year, setYear] = useState<number>();
  const [month, setMonth] = useState<number>();

  useEffect(() => {
    if (calendarApi) setCurrentValues();
  }, [calendarApi]);

  const handleJumpToDate = () => {
    const date = new Date(year, month, 1).toISOString();
    calendarApi.gotoDate(date);
    setCurrentValues();
  };

  const handleNextMonth = () => {
    calendarApi.next();
    setCurrentValues();
  };

  const handlePreviousMonth = () => {
    calendarApi.prev();
    setCurrentValues();
  };

  const handleToday = () => {
    calendarApi.today();
    setCurrentValues();
  };

  const handleYearChange = (value: number) => {
    setYear((state) => state + value);
  };

  const setCurrentValues = () => {
    const currentDate = calendarApi.getDate();
    const calendarMonth = currentDate.getMonth();
    const calendarYear = currentDate.getFullYear();

    setIsToday(
      getFormattedDate({ date: Date.now(), dateFormat: 'M.y' }) ===
        `${calendarMonth + 1}.${calendarYear}`
    );
    setYear(currentDate.getFullYear());
    setMonth(currentDate.getMonth());
  };

  const months = Array.from(Array(12), (_, i) => i).map((mon) => {
    const value = getFormattedDate({
      date: new Date(2000, mon, 1),
      dateFormat: 'MMM',
      options: { locale: dateFnsLocales[language] }
    });
    return value.charAt(0).toUpperCase() + value.slice(1);
  });

  if (!calendarApi) return null;

  return (
    <div className="header">
      <div className="title">
        <DisplayFormattedDate
          date={calendarApi.getDate()}
          dateFormat="LLLL yyyy"
          dateFormatOptions={{ locale: dateFnsLocales[language] }}
        />
      </div>

      <div className="today">
        <Button
          category="lighter"
          disabled={isToday}
          onClick={handleToday}
          size="header"
          text={messages.eventCalendarToday}
        />
      </div>

      <div className="month-navigator">
        <Button
          aria-label={messages.commonPreviousMonth}
          category="lighter"
          data-tooltip={messages.commonPreviousMonth}
          icon="chevron-left"
          iconStyle={{ fontSize: '1.3rem' }}
          onClick={handlePreviousMonth}
          size="header"
          style={{
            borderRadius: `${theme.buttonBorderRadius} 0 0 ${theme.buttonBorderRadius}`,
            marginRight: 1
          }}
        />
        <Button
          aria-label={messages.commonNextMonth}
          category="lighter"
          data-tooltip={messages.commonNextMonth}
          icon="chevron-right"
          iconStyle={{ fontSize: '1.3rem' }}
          onClick={handleNextMonth}
          size="header"
          style={{
            borderRadius: `0 ${theme.buttonBorderRadius} ${theme.buttonBorderRadius} 0`
          }}
        />
      </div>

      <div className="month-dropdown">
        <Dropdown
          align="right"
          category="lighter"
          icon="calendar-check"
          showArrow={true}
          size="header"
          styleContentButtons={false}
          title={messages.eventCalendarSelectMonth}
          titleStyle={{ fontWeight: 600 }}
        >
          <div>
            <div className="year-selector" data-keep-open={true}>
              <Button
                category="lighter"
                icon="angle-double-left"
                onClick={() => handleYearChange(-1)}
                size="small"
                style={{ borderRadius: theme.buttonBorderRadius }}
              />
              <div className="year-selector-title">{year}</div>
              <Button
                category="lighter"
                icon="angle-double-right"
                onClick={() => handleYearChange(1)}
                size="small"
                style={{ borderRadius: theme.buttonBorderRadius }}
              />
            </div>

            <div className="month-selector" data-keep-open={true}>
              {months.map((mon, i) => (
                <Button
                  active={i === month}
                  category="transparent"
                  key={i}
                  onClick={() => setMonth(i)}
                  text={mon}
                />
              ))}
            </div>

            <div className="submit">
              <Button category="light" text={messages.commonClose} />
              <Button
                category="success"
                onClick={handleJumpToDate}
                text={messages.commonSelect}
              />
            </div>
          </div>
        </Dropdown>
      </div>

      <style jsx>{`
        .header {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          padding: 20px 20px 0;
        }
        .title {
          color: ${theme.textColorLight};
          flex-grow: 1;
          font-size: 2rem;
          font-weight: 700;
          text-transform: capitalize;
          padding: 0 3px;
        }
        .year-selector {
          align-items: center;
          color: ${theme.textColorLighter};
          display: flex;
          gap: 10px;
          justify-content: space-between;
          padding: 5px 15px 0;
        }
        .year-selector-title {
          font-size: 1.5rem;
          font-weight: 700;
        }
        .month-selector {
          border: 1px solid ${theme.lightGreyDarkActive};
          border-radius: 5px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          margin: 15px 15px;
        }
        .month-selector > :global(button) {
          border: 1px solid ${theme.lightGreyDarkActive};
          border-width: 1px 0 0 1px;
          border-radius: 0px;
        }
        .month-selector > :global(button:nth-child(4n - 3)) {
          border-left-width: 0;
        }
        .month-selector > :global(button:nth-child(-n + 4)) {
          border-top-width: 0;
        }
        .submit {
          align-items: flex-end;
          border-top: 1px solid ${theme.lightGreyDark};
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          padding: 13px 15px 5px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventCalendarHeader;
